/**
 * Maps each event category (from server EVENT_CATEGORIES / WorldMapCopy) to a Tabler icon.
 * Event title + description are classified server-side to one of these categories so the
 * logo/icon name matches the event theme (e.g. baseball/sport → Sport & Fitness → ball-baseball).
 * Used in React components (e.g. Friends event chat avatars) and for map pin/popup SVG strings.
 */
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server.browser';
import * as TablerIcons from '@tabler/icons-react';
import {
  IconBallBaseball,
  IconMusic,
  IconUsers,
  IconPalette,
  IconMoodSmile,
  IconToolsKitchen,
  IconDeviceLaptop,
  IconSchool,
  IconUsersGroup,
  IconHeart,
  IconDeviceGamepad,
  IconTrees,
  IconBriefcase,
  IconMovie,
  IconMicrophone,
  IconGlassCocktail,
  IconHeartbeat,
  IconBook,
  IconCamera,
  IconMapPin,
  IconShirt,
  IconAtom,
  IconCross,
  IconFlag,
  IconScissors,
  IconPresentation,
  IconConfetti,
  IconHeartHandshake,
  IconCalendar,
} from '@tabler/icons-react';

const EVENT_CATEGORY_TABLER_ICONS = {
  'Sport & Fitness': IconBallBaseball,
  'Music & Concert': IconMusic,
  'Networking & Professional': IconUsers,
  'Arts & Culture': IconPalette,
  'Comedy & Entertainment': IconMoodSmile,
  'Food & Drink': IconToolsKitchen,
  'Tech & Startup': IconDeviceLaptop,
  'Education & Workshop': IconSchool,
  'Family & Kids': IconUsersGroup,
  'Volunteering & Community': IconHeart,
  'Gaming & Esports': IconDeviceGamepad,
  'Outdoor & Adventure': IconTrees,
  'Business & Career': IconBriefcase,
  'Film & Cinema': IconMovie,
  'Theater & Performing Arts': IconMicrophone,
  'Nightlife & Party': IconGlassCocktail,
  'Wellness & Mindfulness': IconHeartbeat,
  'Book & Reading': IconBook,
  'Photography & Creative': IconCamera,
  'Travel & Exploration': IconMapPin,
  'Fashion & Style': IconShirt,
  'Science & Innovation': IconAtom,
  'Religion & Spirituality': IconCross,
  'Politics & Activism': IconFlag,
  'Dating & Social': IconHeart,
  'Hobby & Craft': IconScissors,
  'Conference & Summit': IconPresentation,
  'Festival & Fair': IconConfetti,
  'Fundraiser & Charity': IconHeartHandshake,
  'Other': IconCalendar,
};

const DEFAULT_ICON = IconCalendar;

/** Cache SVG strings by category and size to avoid re-rendering. */
const _svgCache = {};

/** Pre-warm SVG cache for all categories and map sizes so every icon uses Tabler. */
const CATEGORY_KEYS = Object.keys(EVENT_CATEGORY_TABLER_ICONS);
const MAP_SIZES = [20, 18, 24];
function warmTablerSvgCache() {
  CATEGORY_KEYS.forEach((cat) => {
    MAP_SIZES.forEach((size) => {
      const key = `${cat}-${size}`;
      if (_svgCache[key]) return;
      try {
        const IconComp = EVENT_CATEGORY_TABLER_ICONS[cat];
        const svgEl = React.createElement(IconComp, { size, stroke: 2 });
        const spanEl = React.createElement('span', { style: { color: 'white', display: 'inline-flex' } }, svgEl);
        let html = renderToStaticMarkup(spanEl);
        html = html.replace(/\bstroke="currentColor"/gi, 'stroke="white"').replace(/\bfill="currentColor"/gi, 'fill="white"');
        _svgCache[key] = html;
      } catch (e) {
        console.warn('[eventCategoryTablerIcons] warm cache failed for:', cat, size, e);
      }
    });
  });
}

/**
 * @param {string} [category] - Event category name (e.g. from eventCategoryAgent)
 * @returns {import('react').ComponentType<{ size?: number; stroke?: number; className?: string }>} Tabler icon component
 */
export function getEventCategoryTablerIcon(category) {
  const key = category && typeof category === 'string' ? category.trim() : '';
  return EVENT_CATEGORY_TABLER_ICONS[key] || DEFAULT_ICON;
}

/**
 * Returns SVG markup for the category icon (white stroke) for use in DOM innerHTML (e.g. map pins).
 * @param {string} [category] - Event category name
 * @param {number} [size=20] - Icon size in px
 * @returns {string} HTML string (span wrapping svg with white icon)
 */
export function getEventCategoryIconSvgTabler(category, size = 20) {
  const key = `${category || 'Other'}-${size}`;
  if (_svgCache[key]) return _svgCache[key];
  warmTablerSvgCache();
  if (_svgCache[key]) return _svgCache[key];
  let html;
  try {
    const IconComp = getEventCategoryTablerIcon(category);
    const svgEl = React.createElement(IconComp, { size, stroke: 2 });
    const spanEl = React.createElement('span', { style: { color: 'white', display: 'inline-flex' } }, svgEl);
    html = renderToStaticMarkup(spanEl);
    // Force white stroke/fill so icons show on colored pins regardless of DOM cascade
    html = html.replace(/\bstroke="currentColor"/gi, 'stroke="white"').replace(/\bfill="currentColor"/gi, 'fill="white"');
    _svgCache[key] = html;
  } catch (e) {
    console.warn('[eventCategoryTablerIcons] Tabler render failed for category:', category, e);
    // Fallback: use simple calendar SVG so something shows
    html = `<span style="color:white;display:inline-flex"><svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>`;
    _svgCache[key] = html;
  }
  return html;
}

/**
 * Returns SVG markup for a Tabler icon by its kebab-case name
 * (e.g. "ball-baseball", "music", "glass-cocktail"), for use
 * in DOM innerHTML (map pins, popups, etc).
 * Falls back to a calendar icon when the name is unknown.
 *
 * @param {string} [iconName] - Tabler icon kebab-case name
 * @param {number} [size=20] - Icon size in px
 * @returns {string} HTML string (span wrapping svg with white stroke/fill)
 */
export function getTablerIconSvgByName(iconName, size = 20) {
  const safeName = (iconName || '').toString().trim();
  const cacheKey = `name:${safeName || 'calendar'}-${size}`;
  if (_svgCache[cacheKey]) return _svgCache[cacheKey];

  let IconComp = DEFAULT_ICON;
  if (safeName) {
    // Support both "ball-baseball" and "icon-ball-baseball" style names
    const base = safeName.toLowerCase().startsWith('icon-') ? safeName.slice(5) : safeName;
    // Convert kebab-case to PascalCase with Icon prefix: "ball-baseball" -> "IconBallBaseball"
    const pascal = 'Icon' + base
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
    if (TablerIcons[pascal]) {
      IconComp = TablerIcons[pascal];
    }
  }

  try {
    const svgEl = React.createElement(IconComp, { size, stroke: 2 });
    const spanEl = React.createElement('span', { style: { color: 'white', display: 'inline-flex' } }, svgEl);
    let html = renderToStaticMarkup(spanEl);
    html = html.replace(/\bstroke="currentColor"/gi, 'stroke="white"').replace(/\bfill="currentColor"/gi, 'fill="white"');
    _svgCache[cacheKey] = html;
    return html;
  } catch (e) {
    console.warn('[eventCategoryTablerIcons] Tabler render failed for icon name:', safeName, e);
    const html = getEventCategoryIconSvgTabler('Other', size);
    _svgCache[cacheKey] = html;
    return html;
  }
}

/** Tag list for matching event text to Tabler icon name (same as map). Order matters: more specific first. */
export const EVENT_TABLER_ICON_TAGS = [
  { name: 'piano', tags: ['piano', 'jazz', 'blues', 'symphony', 'classical', 'keyboard', 'orchestra', 'instrumental'] },
  { name: 'guitar-pick', tags: ['guitar', 'acoustic', 'folk', 'country', 'strings', 'pick', 'singer', 'songwriter'] },
  { name: 'microphone', tags: ['microphone', 'karaoke', 'singing', 'vocal', 'live', 'open', 'mic', 'standup', 'podcast', 'talk', 'speaker', 'performance', 'show'] },
  { name: 'music', tags: ['music', 'concert', 'band', 'live', 'rock', 'pop', 'festival', 'opera', 'dj', 'hip', 'hop', 'rap', 'electronic', 'edm', 'gig', 'song', 'tour'] },
  { name: 'ball-baseball', tags: ['baseball', 'sport', 'sports', 'game', 'ball', 'mariners', 'mlb', 'stadium', 'arena', 'team'] },
  { name: 'ball-basketball', tags: ['basketball', 'nba', 'hoops', 'bball'] },
  { name: 'ball-football', tags: ['football', 'nfl', 'soccer', 'mls'] },
  { name: 'device-gamepad', tags: ['gaming', 'esports', 'video', 'gamer', 'playstation', 'xbox', 'nintendo'] },
  { name: 'movie', tags: ['movie', 'film', 'cinema', 'theater', 'theatre', 'broadway', 'screening', 'documentary'] },
  { name: 'camera', tags: ['photo', 'photography', 'camera', 'pictures', 'portrait'] },
  { name: 'palette', tags: ['art', 'arts', 'painting', 'gallery', 'museum', 'exhibit', 'creative', 'craft'] },
  { name: 'glass-cocktail', tags: ['cocktail', 'bar', 'drinks', 'wine', 'beer', 'nightlife', 'happy', 'hour', 'brewery', 'pub'] },
  { name: 'tools-kitchen', tags: ['food', 'cooking', 'kitchen', 'chef', 'restaurant', 'dining', 'brunch', 'culinary', 'tasting', 'dinner', 'lunch', 'breakfast'] },
  { name: 'book', tags: ['book', 'books', 'reading', 'literature', 'author', 'poetry', 'bookclub'] },
  { name: 'school', tags: ['school', 'education', 'class', 'workshop', 'seminar', 'lecture', 'learning', 'course', 'training', 'tutorial'] },
  { name: 'briefcase', tags: ['business', 'career', 'networking', 'professional', 'entrepreneur', 'startup', 'conference', 'summit'] },
  { name: 'mood-smile', tags: ['comedy', 'funny', 'laugh', 'humor', 'entertainment'] },
  { name: 'heart', tags: ['heart', 'dating', 'social', 'love', 'charity', 'volunteer', 'community', 'fundraiser'] },
  { name: 'users', tags: ['meetup', 'networking', 'social', 'community', 'group', 'people'] },
  { name: 'confetti', tags: ['festival', 'fair', 'celebration', 'party', 'birthday', 'wedding'] },
  { name: 'trees', tags: ['outdoor', 'nature', 'hiking', 'hike', 'park', 'camping', 'garden', 'trail'] },
  { name: 'map-pin', tags: ['travel', 'trip', 'exploration', 'adventure', 'tour'] },
  { name: 'presentation', tags: ['conference', 'summit', 'presentation', 'talk', 'tech'] },
  { name: 'device-laptop', tags: ['tech', 'coding', 'developer', 'programming', 'hackathon', 'software', 'digital', 'ai'] },
  { name: 'flag', tags: ['politics', 'activism', 'rally', 'protest', 'vote'] },
  { name: 'cross', tags: ['church', 'faith', 'religious', 'spiritual', 'prayer'] },
  { name: 'calendar', tags: ['event', 'date', 'schedule', 'meetup', 'calendar'] }
];

/**
 * Get Tabler icon name (kebab-case) for an event from its title, description, tags (same logic as map).
 * @param {{ title?: string; name?: string; description?: string; tags?: string[]; apiSegment?: string }} event
 * @returns {string} Tabler icon name e.g. 'music', 'piano', 'ball-baseball'
 */
export function getEventTablerIconName(event) {
  if (!event) return 'calendar';
  const raw = [
    event.title || event.name || '',
    event.description || '',
    event.about || event.summary || event.info || '',
    (event.apiSegment || '').toString(),
    (event.eventCategory || '').toString(),
    (event.venueName || '').toString(),
    (event.groupName || '').toString(),
    Array.isArray(event.tags) ? event.tags.join(' ') : (event.tags || '').toString()
  ].join(' ');
  const text = raw.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 'calendar';
  const words = new Set(text.split(' ').filter(Boolean));
  let bestName = 'calendar';
  let bestScore = 0;
  for (const { name, tags } of EVENT_TABLER_ICON_TAGS) {
    let score = 0;
    for (const tag of tags) {
      if (words.has(tag)) score += 3;
      else if (text.includes(tag)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestName = name;
    }
  }
  return bestName;
}

export default EVENT_CATEGORY_TABLER_ICONS;
